import { yupResolver } from '@hookform/resolvers/yup'
import { EBankStatementsRequiredFor, EDecision, EFinancingProgram, getEDecisionList } from '@if/core/types'
import { formatSignedNumber } from '@if/core/utils'
import { Alert, Grid, InputAdornment } from '@mui/material'
import { CreditDecisionOverrideLimits } from '@src/data/types/CreditDecisionOverrideLimits'
import { get } from 'lodash-es'
import { memo, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ActionsDialog, InputTextField, SelectComponent } from '../../../components'
import MultipleCheckboxList from '../../../components/MultipleCheckboxList'
import { TRootState, useAppSelector } from '../../../data/store'
import { appSelectors } from '../../../data/store/AppStore'
import { creditSelectors } from '../../../data/store/CreditApplication'
import { personalLoanworksheetSelectors } from '../../../data/store/PersonalLoanWorksheet'
import {
  Constants,
  CreditApplication,
  getEExistingCarLoanStatusList,
  MIN_RATE_CREDIT_RSLA_SILVERPLAN,
  SelectValueListItem,
  ValueListItem,
} from '../../../data/types'
import { FinalCreditDecision, WrapperFinalCreditDecisionSchema } from '../../../data/types/CreditDecisionSchema'
import { reportErrorToConsole } from '../../../services/error-logger'

interface Props {
  creditApplication: CreditApplication
  creditApplicationDecisionOverrideLimits: CreditDecisionOverrideLimits | null
  open: boolean
  canApprove: boolean
  financingProgramId: EFinancingProgram
  onCancel: () => void
  onConfirm: (data: FinalCreditDecision) => void
}

const CreditDecisionDialog = ({
  open,
  creditApplication,
  creditApplicationDecisionOverrideLimits,
  canApprove,
  financingProgramId,
  onCancel,
  onConfirm,
}: Props) => {
  const { t } = useTranslation()
  const creditRefusalReasons = useAppSelector<ValueListItem[]>(appSelectors.getCreditRefusalReasons)

  const creditDecision = {
    creditApplicationId: creditApplication.id,
    interestRate: creditApplication.finalCreditDecision.interestRate,
    cashdownPercent: creditApplication.decisionOverrides?.cashdownPercent,
    maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
    maxAmountFinanced: creditApplication.finalCreditDecision.maxAmountFinanced,
    maxTermDuration: creditApplication.finalCreditDecision.maxTermDuration,
    revisionId: creditApplication.currentRevisionId,
    decision: creditApplication.finalCreditDecision.decision,
    bankStatementsRequiredFor: creditApplication.decisionOverrides?.bankStatementsRequiredFor,
    noCoAplicant: !creditApplication.coApplicant,
    existingCarLoanStatus: creditApplication.decisionOverrides?.existingCarLoanStatus,
    loanPurpose: creditApplication.loanPurposeId,
  }

  const financingProgramConfig = get(
    Constants.financingProgramConfigs,
    financingProgramId?.toLowerCase() ?? 'default',
    Constants.financingProgramConfigs.default,
  )

  const creditRslaPlanEnum = useAppSelector((state: TRootState) =>
    appSelectors.getPlanEnumForProgram(state, EFinancingProgram.CreditRsla),
  )

  const computeMinRateBasedOnPlanForCreditRsla = () => {
    if (
      creditApplication.normsCreditDecision?.planName &&
      creditApplication.normsCreditDecision?.planName.toString() === creditRslaPlanEnum.creditRslaSilver.id
    ) {
      return MIN_RATE_CREDIT_RSLA_SILVERPLAN
    } else {
      return financingProgramConfig.minInterestRate
    }
  }

  // Agent must be able to override the minimum interest rate, but cannot go below the minimum interest rate
  // defined in the financing program config unless the normsCreditDecision has a already a lower interest rate.
  const minInterestRate =
    financingProgramId === EFinancingProgram.CreditRsla
      ? computeMinRateBasedOnPlanForCreditRsla()
      : Math.min(
          creditApplication.normsCreditDecision?.interestRate ?? financingProgramConfig.minInterestRate,
          financingProgramConfig.minInterestRate,
        )
  const maxInterestRate =
    creditApplication.normsCreditDecision?.interestRateModifier !== undefined
      ? financingProgramConfig.maxInterestRate + creditApplication.normsCreditDecision.interestRateModifier
      : financingProgramConfig.maxInterestRate

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<FinalCreditDecision>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: creditDecision,
    resolver: yupResolver(
      WrapperFinalCreditDecisionSchema(
        creditApplicationDecisionOverrideLimits?.paymentUpperLimit ??
          creditApplication.normsCreditDecision?.maxPmtAmount ??
          0,
        creditApplicationDecisionOverrideLimits?.paymentLowerLimit ?? 0,
        minInterestRate,
        maxInterestRate ?? 100,
      ),
    ),
  })

  const decision = watch('decision')
  const createdWithPromotion = useAppSelector(creditSelectors.createdWithPromotion)
  const hasConfirmedPromotion = useAppSelector(personalLoanworksheetSelectors.hasConfirmedPromotion)
  const isWorksheetCreated = useAppSelector(personalLoanworksheetSelectors.isCreated)
  const hasMerchantRateModifier = useAppSelector(creditSelectors.hasMerchantRateModifier)

  const isInterestRateFieldDisabled = () => {
    if (isWorksheetCreated && hasConfirmedPromotion) return true
    if (isWorksheetCreated && !hasConfirmedPromotion) return false
    if (createdWithPromotion && !hasConfirmedPromotion) return true
    return false
  }

  useEffect(() => {
    if (decision !== EDecision.Refused) setValue('refusalReasonCodes', [])
  }, [decision, setValue])

  const [resetList, setReset] = useState<boolean>(false)

  const selectedDecision = watch('decision')
  const [originalDecision] = useState<EDecision>(selectedDecision)

  const showDetail = selectedDecision !== EDecision.Approved || canApprove

  const existingCarLoanStatusesList = useMemo(
    () => [
      { label: '', value: '' },
      ...getEExistingCarLoanStatusList().map((x) => ({
        label: t(`credit.existingCarLoanStatus.${x}`),
        value: x,
      })),
    ],
    [t],
  )

  const decisionValuesList = useMemo(
    () =>
      getEDecisionList()
        .filter((x) => x !== EDecision.Pending)
        .map((x) => ({ label: t(`EDecision.${x}`, { ns: 'enum' }), value: x })),
    [t],
  )

  const bankStatementValuesList = useMemo(() => {
    const ret = [] as SelectValueListItem[]
    ret.push({ label: '', value: '' })
    ret.push({
      label: t(`EBankStatementsRequiredFor.${EBankStatementsRequiredFor.Applicant}`, { ns: 'enum' }),
      value: EBankStatementsRequiredFor.Applicant,
    })
    if (creditApplication.coApplicant) {
      ret.push({
        label: t(`EBankStatementsRequiredFor.${EBankStatementsRequiredFor.Coapplicant}`, { ns: 'enum' }),
        value: EBankStatementsRequiredFor.Coapplicant,
      })
      ret.push({
        label: t(`EBankStatementsRequiredFor.${EBankStatementsRequiredFor.Both}`, { ns: 'enum' }),
        value: EBankStatementsRequiredFor.Both,
      })
    }
    return ret
  }, [creditApplication.coApplicant, t])

  useEffect(() => {
    if (financingProgramId === EFinancingProgram.Personal) {
      setValue('existingCarLoanStatus', null)
      setValue('cashdownPercent', null)
    }
  }, [financingProgramId, setValue])
  const shoulDisplayDialogFields = useAppSelector(
    creditSelectors.getFinancingProgramConfigs,
  ).useCreditDecisionDialogFields

  return (
    <ActionsDialog
      disabled={selectedDecision === EDecision.Approved && !canApprove}
      title={`${t('credit.icebergDecision')} - ${t(`EDecision.${creditApplication.finalCreditDecision.decision}`, { ns: 'enum' })}`}
      onCancel={() => {
        setReset(!resetList)
        onCancel()
      }}
      onConfirm={handleSubmit(onConfirm, reportErrorToConsole)}
      open={open}
    >
      <Grid
        container
        spacing={2}
        sx={[
          {
            mt: 1,
          },
          selectedDecision === EDecision.Refused
            ? {
                maxWidth: '100%',
              }
            : {
                maxWidth: 500,
              },
        ]}
      >
        <Grid size={12}>
          <SelectComponent
            label={t('credit.decision')}
            items={decisionValuesList}
            {...register('decision')}
            error={errors?.decision}
            disabled={originalDecision === EDecision.Refused || originalDecision === EDecision.Error}
          />
          {selectedDecision === EDecision.Approved && !canApprove && (
            <Grid sx={{ mt: 1 }} size={12}>
              <Alert severity="error">{t('credit.taskAndDocumentRequiredMissing')}</Alert>
            </Grid>
          )}
          {selectedDecision === EDecision.Refused && (
            <MultipleCheckboxList
              name="refusalReasonCodes"
              control={control}
              title={t('credit.refusalReason')}
              content={creditRefusalReasons}
              defaultValues={creditApplication.finalCreditDecision.refusalReasonCodes ?? []}
              reset={resetList}
            />
          )}
        </Grid>
        {selectedDecision !== EDecision.Refused && showDetail && shoulDisplayDialogFields && (
          <>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <InputTextField
                error={errors?.interestRate}
                {...register('interestRate')}
                label={
                  hasMerchantRateModifier
                    ? t('credit.hasRateModifier', {
                        rate: formatSignedNumber(creditApplication.normsCreditDecision?.interestRateModifier),
                      })
                    : t('credit.interestRate')
                }
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="start">%</InputAdornment>,
                  },
                }}
                required
                disabled={isInterestRateFieldDisabled()}
                forceBaseColor={hasMerchantRateModifier!}
                color={hasMerchantRateModifier ? 'warning' : 'primary'}
              />
            </Grid>

            {financingProgramId === EFinancingProgram.Auto && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputTextField
                  error={errors?.cashdownPercent}
                  {...register('cashdownPercent')}
                  label={t('credit.cash')}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    },
                  }}
                />
              </Grid>
            )}

            {financingProgramId === EFinancingProgram.Auto && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <SelectComponent
                  label={t('credit.bankStatements')}
                  disabled={selectedDecision !== EDecision.Approved}
                  items={bankStatementValuesList}
                  {...register('bankStatementsRequiredFor')}
                  error={errors?.decision}
                />
              </Grid>
            )}

            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <InputTextField
                error={errors?.maxPmtAmount}
                {...register('maxPmtAmount')}
                label={t('credit.maxPayment')}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                }}
                required
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <InputTextField
                {...register('maxTermDuration')}
                label={t('credit.maxTerme')}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="start">{t('credit.months')}</InputAdornment>,
                  },
                }}
                disabled
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <InputTextField
                error={errors?.maxAmountFinanced}
                {...register('maxAmountFinanced')}
                label={t('credit.authorizedLoanAmount')}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                }}
              />
            </Grid>

            {financingProgramId === EFinancingProgram.Auto && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <SelectComponent
                  label={t('credit.jointVehicule')}
                  items={existingCarLoanStatusesList}
                  {...register('existingCarLoanStatus')}
                  error={errors?.existingCarLoanStatus}
                  disabled={!creditApplication.coApplicant}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </ActionsDialog>
  )
}

export default memo(CreditDecisionDialog)
